/**
 * Create/edit dialog for a context source.
 *
 * `source === null` is create; a source is edit. The parent owns the write —
 * `onSave` is the only path out — so this component stays drivable from the
 * functional tier with no backend of its own.
 *
 * Validation mirrors the server's caps so the save button disables on
 * out-of-bounds input rather than round-tripping a guaranteed 422.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@localagent/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@localagent/ui/components/dialog';
import { Input } from '@localagent/ui/components/input';
import { Label } from '@localagent/ui/components/label';
import type {
  ContextSource,
  ContextSourceDataSource,
  ContextSourcePlacement,
  ContextSourceScope,
  ContextSourceType,
} from '@localagent/sdk';

// These MUST match the server's caps exactly — `MaxContextSourceNameRunes` and
// `MaxContextSourceBodyRunes` in `go/internal/context/source_models.go`, which
// are what `POST/PATCH /v1/context/sources` enforces with a 422.
//
// The name cap was 200 here against a server cap of 100: a 150-character name
// typed freely, passed client validation, enabled Save, and earned a raw 422
// the user could not have predicted. That is the INVERSE of the defect the
// rune-counting comment below fixes — the counting was right, the number was
// wrong — and nothing caught it because no story or e2e exercised the name cap.
// If either server constant changes, change it here in the same commit.
const MAX_NAME_LEN = 100;
const MAX_BODY_LEN = 10000;

/**
 * Count Unicode code points, NOT UTF-16 code units.
 *
 * The server caps bodies with Postgres `char_length` / Go rune counts, both of
 * which count code points. `"…".length` counts UTF-16 units, so every
 * astral-plane character (emoji, many CJK extension glyphs) counts twice — a
 * body of 6000 emoji would be rejected by this client at "12000" while the
 * server would happily accept it. Spreading the string iterates by code point
 * and matches the server exactly.
 *
 * The retired Instructions dialog used `.length` and had exactly that defect;
 * do not "simplify" this back to it.
 *
 * FOR THE SAME REASON THE INPUTS CARRY NO `maxLength` ATTRIBUTE. The DOM
 * attribute counts UTF-16 code units, so it silently truncated pasted emoji at
 * roughly half the runes the server accepts — reintroducing, in the markup,
 * the exact bug this function exists to fix. The rune comparison below already
 * gates Save and gives a better message than a silent clip. Do not add
 * `maxLength` back.
 */
function runeLength(value: string): number {
  return [...value].length;
}

/** The five source types. */
const TYPES: ContextSourceType[] = [
  'environment',
  'user-info',
  'rules',
  'memory',
  'knowledge',
];

// Workspace scope needs a workspace_id, so it is offered only when the editor is given one.
const ACCOUNT_SCOPES: ContextSourceScope[] = ['account', 'user'];
const WORKSPACE_SCOPES: ContextSourceScope[] = ['workspace'];

/**
 * All four placements, with the two deferred to v2 marked (context ADR-005).
 *
 * The deferred pair is rendered DISABLED rather than omitted: a user who can
 * see the axis learns it exists and is coming, and can never select one and
 * receive a 422. This mirrors the server, which recognizes them and rejects
 * them — it does not pretend they are unknown.
 */
const PLACEMENTS: { value: ContextSourcePlacement; deferred: boolean }[] = [
  { value: 'pervasive', deferred: false },
  { value: 'specific-system-prompt', deferred: false },
  { value: 'message-history', deferred: true },
  { value: 'step-scoped', deferred: true },
];

export interface ContextSourceEditorValues {
  name: string;
  type: ContextSourceType;
  scope: ContextSourceScope;
  placement: 'pervasive' | 'specific-system-prompt';
  data_source: 'loaded-inline' | 'referenced';
  body?: string;
  reference_path?: string;
  target_persona?: string;
  workspace_id?: string;
}

export interface ContextSourceEditorProps {
  source: ContextSource | null;
  open: boolean;
  onClose: () => void;
  onSave: (values: ContextSourceEditorValues) => Promise<void>;
  workspaceId?: string;
}

export function ContextSourceEditor({
  source,
  open,
  onClose,
  onSave,
  workspaceId,
}: ContextSourceEditorProps) {
  const scopes = workspaceId ? WORKSPACE_SCOPES : ACCOUNT_SCOPES;
  const defaultScope = scopes[0];
  const [name, setName] = useState('');
  const [type, setType] = useState<ContextSourceType>('rules');
  const [scope, setScope] = useState<ContextSourceScope>(defaultScope);
  const [placement, setPlacement] = useState<
    'pervasive' | 'specific-system-prompt'
  >('pervasive');
  const [dataSource, setDataSource] = useState<'loaded-inline' | 'referenced'>(
    'loaded-inline',
  );
  const [body, setBody] = useState('');
  const [referencePath, setReferencePath] = useState('');
  const [targetPersona, setTargetPersona] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(source?.name ?? '');
    setType(source?.type ?? 'rules');
    setScope(source?.scope ?? defaultScope);
    setPlacement(
      source?.placement === 'specific-system-prompt'
        ? 'specific-system-prompt'
        : 'pervasive',
    );
    setDataSource(source?.data_source === 'referenced' ? 'referenced' : 'loaded-inline');
    setBody(source?.body ?? '');
    setReferencePath(source?.reference_path ?? '');
    setTargetPersona(source?.target_persona ?? '');
    setError(null);
  }, [open, source, defaultScope]);

  /**
   * A `referenced` data source is only meaningful for `knowledge`:
   * large knowledge should not be force-inlined, whereas rules/memory carry a
   * body. Switching away from `knowledge` therefore snaps back to inline
   * rather than leaving an unreachable combination selected.
   */
  const referencedAvailable = type === 'knowledge';
  useEffect(() => {
    if (!referencedAvailable && dataSource === 'referenced') {
      setDataSource('loaded-inline');
    }
  }, [referencedAvailable, dataSource]);

  const nameLen = useMemo(() => runeLength(name), [name]);
  const bodyLen = useMemo(() => runeLength(body), [body]);

  const nameInvalid = nameLen < 1 || nameLen > MAX_NAME_LEN;
  const bodyInvalid =
    dataSource === 'loaded-inline' && (bodyLen < 1 || bodyLen > MAX_BODY_LEN);
  const referenceInvalid =
    dataSource === 'referenced' && referencePath.trim().length === 0;
  const personaInvalid =
    placement === 'specific-system-prompt' && targetPersona.trim().length === 0;
  const invalid =
    nameInvalid || bodyInvalid || referenceInvalid || personaInvalid;

  const handleSave = async () => {
    if (invalid) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        name,
        type,
        scope,
        placement,
        data_source: dataSource,
        ...(dataSource === 'loaded-inline' ? { body } : {}),
        ...(dataSource === 'referenced'
          ? { reference_path: referencePath.trim() }
          : {}),
        ...(placement === 'specific-system-prompt'
          ? { target_persona: targetPersona.trim() }
          : {}),
        ...(workspaceId ? { workspace_id: workspaceId } : {}),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{source ? 'Edit source' : 'New context source'}</DialogTitle>
          <DialogDescription>
            Choose what this source carries, where it comes from, where it lands
            in the prompt, and who it applies to.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor="context-source-name">Name</Label>
            <Input
              id="context-source-name"
              data-testid="context-source-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <Label htmlFor="context-source-type">Type</Label>
              <select
                id="context-source-type"
                data-testid="context-source-type"
                className="h-9 rounded-md bg-muted px-3 text-sm"
                value={type}
                onChange={(e) => setType(e.target.value as ContextSourceType)}
              >
                {TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="context-source-scope">Scope</Label>
              <select
                id="context-source-scope"
                data-testid="context-source-scope"
                className="h-9 rounded-md bg-muted px-3 text-sm"
                value={scope}
                onChange={(e) => setScope(e.target.value as ContextSourceScope)}
              >
                {scopes.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="context-source-placement">Placement</Label>
              <select
                id="context-source-placement"
                data-testid="context-source-placement"
                className="h-9 rounded-md bg-muted px-3 text-sm"
                value={placement}
                onChange={(e) =>
                  setPlacement(
                    e.target.value as 'pervasive' | 'specific-system-prompt',
                  )
                }
              >
                {PLACEMENTS.map((p) => (
                  <option
                    key={p.value}
                    value={p.value}
                    disabled={p.deferred}
                    data-testid={`context-source-placement-option-${p.value}`}
                  >
                    {p.deferred ? `${p.value} (coming in v2)` : p.value}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="context-source-data-source">Data source</Label>
              <select
                id="context-source-data-source"
                data-testid="context-source-data-source"
                className="h-9 rounded-md bg-muted px-3 text-sm"
                value={dataSource}
                onChange={(e) =>
                  setDataSource(e.target.value as ContextSourceDataSource as
                    | 'loaded-inline'
                    | 'referenced')
                }
              >
                <option value="loaded-inline">loaded-inline</option>
                <option
                  value="referenced"
                  disabled={!referencedAvailable}
                  data-testid="context-source-data-source-option-referenced"
                >
                  {referencedAvailable
                    ? 'referenced'
                    : 'referenced (knowledge only)'}
                </option>
              </select>
            </div>
          </div>

          {placement === 'specific-system-prompt' && (
            <div className="flex flex-col gap-1">
              <Label htmlFor="context-source-target-persona">Target persona</Label>
              <Input
                id="context-source-target-persona"
                data-testid="context-source-target-persona"
                value={targetPersona}
                placeholder="An account persona name"
                onChange={(e) => setTargetPersona(e.target.value)}
              />
            </div>
          )}

          {dataSource === 'loaded-inline' ? (
            <div className="flex flex-col gap-1">
              <Label htmlFor="context-source-body">Content</Label>
              <textarea
                id="context-source-body"
                data-testid="context-source-body"
                className="min-h-40 rounded-md bg-muted p-3 text-sm"
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
              <p
                className={`text-xs ${bodyLen > MAX_BODY_LEN ? 'text-destructive' : 'text-muted-foreground'}`}
                data-testid="context-source-body-count"
              >
                {bodyLen}/{MAX_BODY_LEN} characters
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <Label htmlFor="context-source-reference-path">Reference path</Label>
              <Input
                id="context-source-reference-path"
                data-testid="context-source-reference-path"
                value={referencePath}
                placeholder="docs/architecture.md"
                onChange={(e) => setReferencePath(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Read fresh each turn. If there is no workspace or connected
                device to read it from, the source contributes nothing that turn
                rather than failing.
              </p>
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive" data-testid="context-source-dialog-error">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            data-testid="context-source-save"
            disabled={invalid || saving}
            onClick={() => void handleSave()}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
